import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { API_URL } from "../../Constants";
import { toast } from "react-toastify";
import { PlusIcon, XMarkIcon, BeakerIcon, ArrowPathIcon } from "@heroicons/react/24/solid";
import ConfirmModal from "./ConfirmModal";

// Mirrors LeadFieldMappingEngine.defaultIvrMapping() on the backend - what's actually in
// effect when a company hasn't configured anything yet. Shown as the starting point so
// editing always starts from "what's really happening right now", not a blank table.
const DEFAULT_IVR_MAPPING = [
  { sourceField: "sourceNumber", targetField: "phone", defaultValue: "" },
  { sourceField: "leadName", targetField: "name", defaultValue: "" },
];

const emptyRow = () => ({ sourceField: "", targetField: "", defaultValue: "" });

/**
 * Maps an ingestion point's incoming payload fields onto Lead fields, for one
 * companyId + ingestionPoint + endpointKey. Used both for IVR GURU's reconciliation feed
 * (ingestionPoint="IVR", endpointKey="default") and for each individual webhook
 * integration (ingestionPoint="IVR", endpointKey=<that integration's endpointKey>).
 */
const LeadFieldMappingEditor = ({ ingestionPoint, endpointKey }) => {
  const { user } = useContext(AuthContext);
  const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
  const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;

  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null); // existing LeadFieldMappingConfig, or null = using default
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);

  const [targetFields, setTargetFields] = useState([]); // [{key, label, type}]
  const [discoveredFields, setDiscoveredFields] = useState([]); // [{sourceField, exampleValue}]
  const [samples, setSamples] = useState([]); // [{receivedAt, payload}]

  const [showReset, setShowReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  // test panel
  const [testMode, setTestMode] = useState("latest"); // "latest" | "custom"
  const [customPayload, setCustomPayload] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);

  useEffect(() => {
    if (companyId) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, ingestionPoint, endpointKey]);

  const fetchAll = async () => {
    setLoading(true);
    setTestResult(null);
    setTestError(null);
    try {
      const [configRes, targetsRes, discoveredRes, samplesRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/lead-mapping/company/${companyId}/${ingestionPoint}/${endpointKey}`, {
          headers: authHeader,
        }),
        axios.get(`${API_URL}/api/lead-mapping/targets/${companyId}`, { headers: authHeader }),
        axios.get(`${API_URL}/api/lead-mapping/samples/${companyId}/${ingestionPoint}/${endpointKey}/fields`, {
          headers: authHeader,
        }),
        axios.get(`${API_URL}/api/lead-mapping/samples/${companyId}/${ingestionPoint}/${endpointKey}?limit=5`, {
          headers: authHeader,
        }),
      ]);

      if (configRes.status === "fulfilled") {
        const cfg = configRes.value.data;
        setConfig(cfg);
        setRows(cfg.mappings?.length ? cfg.mappings : [emptyRow()]);
      } else {
        setConfig(null);
        setRows(DEFAULT_IVR_MAPPING.map((r) => ({ ...r })));
      }

      setTargetFields(targetsRes.status === "fulfilled" ? targetsRes.value.data : []);
      setDiscoveredFields(discoveredRes.status === "fulfilled" ? discoveredRes.value.data : []);
      setSamples(samplesRes.status === "fulfilled" ? samplesRes.value.data : []);
    } catch {
      toast.error("Failed to load field mapping");
    } finally {
      setLoading(false);
    }
  };

  /** ---------------------------- row editing ---------------------------- */

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (idx) => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length > 0 ? next : [emptyRow()];
    });
  };

  /** Clicking a discovered field fills the first empty sourceField, or adds a new row. */
  const handleUseDiscoveredField = (sourceField) => {
    setRows((prev) => {
      const emptyIdx = prev.findIndex((r) => !r.sourceField.trim());
      if (emptyIdx >= 0) {
        return prev.map((r, i) => (i === emptyIdx ? { ...r, sourceField } : r));
      }
      return [...prev, { sourceField, targetField: "", defaultValue: "" }];
    });
  };

  /** ---------------------------- save / active / reset ---------------------------- */

  const validRows = () => rows.filter((r) => r.sourceField.trim() && r.targetField.trim());

  const handleSave = async () => {
    const mappings = validRows();
    if (mappings.length === 0) {
      toast.error("Add at least one complete mapping (source field + target field)");
      return;
    }
    try {
      setSaving(true);
      await axios.post(
        `${API_URL}/api/lead-mapping`,
        { companyId, ingestionPoint, endpointKey, mappings },
        { headers: authHeader }
      );
      toast.success("Field mapping saved");
      await fetchAll();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save field mapping");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!config) return;
    try {
      setTogglingActive(true);
      await axios.put(
        `${API_URL}/api/lead-mapping/company/${companyId}/${ingestionPoint}/${endpointKey}/active?active=${!config.active}`,
        {},
        { headers: authHeader }
      );
      await fetchAll();
    } catch {
      toast.error("Failed to update mapping status");
    } finally {
      setTogglingActive(false);
    }
  };

  const handleReset = async () => {
    if (!config) return;
    try {
      setResetting(true);
      await axios.delete(`${API_URL}/api/lead-mapping/${config.id}?companyId=${companyId}`, { headers: authHeader });
      toast.success("Reset to the default mapping");
      setConfig(null);
      setRows(DEFAULT_IVR_MAPPING.map((r) => ({ ...r })));
    } catch {
      toast.error("Failed to reset mapping");
    } finally {
      setResetting(false);
      setShowReset(false);
    }
  };

  /** ---------------------------- test ---------------------------- */

  const handleTest = async () => {
    const mappings = validRows();
    if (mappings.length === 0) {
      toast.error("Add at least one complete mapping before testing");
      return;
    }

    let samplePayload;
    if (testMode === "custom") {
      if (!customPayload.trim()) {
        toast.error("Paste a JSON payload to test against");
        return;
      }
      try {
        samplePayload = JSON.parse(customPayload);
      } catch {
        toast.error("That's not valid JSON");
        return;
      }
    }

    try {
      setTesting(true);
      setTestError(null);
      const res = await axios.post(
        `${API_URL}/api/lead-mapping/test`,
        { companyId, ingestionPoint, endpointKey, mappings, samplePayload },
        { headers: authHeader }
      );
      setTestResult(res.data);
    } catch (error) {
      setTestResult(null);
      setTestError(error?.response?.data?.message || "Failed to run the test");
    } finally {
      setTesting(false);
    }
  };

  const handleUseSampleAsCustom = (sample) => {
    setTestMode("custom");
    setCustomPayload(JSON.stringify(sample.payload, null, 2));
  };

  /** ---------------------------- render ---------------------------- */

  if (loading) {
    return <div className="p-4 text-sm text-gray-600 dark:text-gray-300">Loading field mapping...</div>;
  }

  return (
    <div className="p-4 bg-white dark:bg-gray-800 rounded border">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h4 className="text-sm font-semibold">Field mapping</h4>
        <div className="flex items-center gap-2">
          {config && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                config.active
                  ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200"
                  : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
              }`}
            >
              {config.active ? "Custom mapping active" : "Custom mapping saved but disabled"}
            </span>
          )}
          {!config && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              Using default mapping
            </span>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Tell us which field in the incoming payload becomes which lead field. If a source field is missing or
        empty, the default value (if any) is used instead.
      </p>

      {/* mapping rows */}
      {rows.map((row, idx) => (
        <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-3 mb-2 items-end">
          <div>
            {idx === 0 && <label className="block text-xs font-medium mb-1">Payload field</label>}
            <input
              list="discovered-fields-list"
              placeholder="e.g. sourceNumber"
              value={row.sourceField}
              onChange={(e) => updateRow(idx, "sourceField", e.target.value)}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div>
            {idx === 0 && <label className="block text-xs font-medium mb-1">Lead field</label>}
            <select
              value={row.targetField}
              onChange={(e) => updateRow(idx, "targetField", e.target.value)}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"
            >
              <option value="">Select field</option>
              <optgroup label="Lead fields">
                {targetFields
                  .filter((t) => t.type === "STATIC")
                  .map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
              </optgroup>
              {targetFields.some((t) => t.type === "CUSTOM") && (
                <optgroup label="Custom fields">
                  {targetFields
                    .filter((t) => t.type === "CUSTOM")
                    .map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.label}
                      </option>
                    ))}
                </optgroup>
              )}
            </select>
          </div>
          <div>
            {idx === 0 && <label className="block text-xs font-medium mb-1">Default value (optional)</label>}
            <input
              placeholder="Used when the field is missing"
              value={row.defaultValue}
              onChange={(e) => updateRow(idx, "defaultValue", e.target.value)}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"
            />
          </div>
          <button onClick={() => removeRow(idx)} className="p-2 text-gray-400 hover:text-red-500" title="Remove">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      ))}

      <datalist id="discovered-fields-list">
        {discoveredFields.map((f) => (
          <option key={f.sourceField} value={f.sourceField} />
        ))}
      </datalist>

      <div className="flex flex-wrap items-center gap-3 mt-2">
        <button onClick={addRow} className="text-[#673AB7] text-sm hover:underline flex items-center">
          <PlusIcon className="h-4 w-4 mr-1" />
          Add mapping
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm"
        >
          {saving ? "Saving..." : "Save mapping"}
        </button>
        {config && (
          <>
            <button
              onClick={handleToggleActive}
              disabled={togglingActive}
              className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              {config.active ? "Disable" : "Enable"}
            </button>
            <button
              onClick={() => setShowReset(true)}
              className="px-3 py-2 text-sm bg-red-50 text-red-600 dark:bg-red-900 dark:text-red-200 rounded hover:bg-red-100 dark:hover:bg-red-800"
            >
              Reset to default
            </button>
          </>
        )}
      </div>

      {/* discovered fields from real traffic */}
      <div className="mt-5 pt-4 border-t dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Fields seen in real payloads
          </h5>
          <button onClick={fetchAll} className="text-xs text-[#673AB7] hover:underline flex items-center gap-1">
            <ArrowPathIcon className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>
        {discoveredFields.length === 0 ? (
          <p className="text-xs text-gray-500">
            Nothing captured yet on this endpoint. Once a real call comes in, its field names will show up here
            to pick from.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {discoveredFields.map((f) => (
              <button
                key={f.sourceField}
                onClick={() => handleUseDiscoveredField(f.sourceField)}
                title={`Example: ${f.exampleValue}`}
                className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded hover:bg-[#F3E8FF] dark:hover:bg-blue-900"
              >
                {f.sourceField} <span className="text-gray-400">= {String(f.exampleValue).slice(0, 20)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* recent raw payloads */}
      {samples.length > 0 && (
        <div className="mt-4">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Recent payloads</h5>
          <div className="space-y-2">
            {samples.map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-xs bg-gray-50 dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500">{new Date(s.receivedAt).toLocaleString()}</span>
                <button onClick={() => handleUseSampleAsCustom(s)} className="text-[#673AB7] hover:underline">
                  Use for test
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* test panel */}
      <div className="mt-5 pt-4 border-t dark:border-gray-700">
        <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1">
          <BeakerIcon className="h-3.5 w-3.5" />
          Test this mapping
        </h5>

        <div className="flex items-center gap-4 mb-2 text-sm">
          <label className="flex items-center gap-1">
            <input
              type="radio"
              checked={testMode === "latest"}
              onChange={() => setTestMode("latest")}
            />
            Most recent captured payload
          </label>
          <label className="flex items-center gap-1">
            <input type="radio" checked={testMode === "custom"} onChange={() => setTestMode("custom")} />
            Paste a payload
          </label>
        </div>

        {testMode === "custom" && (
          <textarea
            value={customPayload}
            onChange={(e) => setCustomPayload(e.target.value)}
            placeholder='{"sourceNumber": "9876543210", "leadName": "Test Caller"}'
            rows={5}
            className="w-full p-2 border rounded font-mono text-xs mb-2 dark:bg-gray-700 dark:text-white"
          />
        )}

        <button
          onClick={handleTest}
          disabled={testing}
          className="px-4 py-2 bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] disabled:opacity-50 text-sm"
        >
          {testing ? "Running..." : "Run test"}
        </button>

        {testError && <p className="text-sm text-red-600 mt-3">{testError}</p>}

        {testResult && (
          <div className="mt-3 text-sm bg-gray-50 dark:bg-gray-900 rounded p-3">
            <p className="font-medium mb-2">This mapping would produce:</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              {["name", "email", "phone", "otherContactNo", "companyName", "reference", "address", "comment", "city", "state", "projectName"]
                .filter((f) => testResult[f])
                .map((f) => (
                  <React.Fragment key={f}>
                    <span className="text-gray-500">{f}</span>
                    <span>{testResult[f]}</span>
                  </React.Fragment>
                ))}
              {testResult.customFields &&
                Object.entries(testResult.customFields).map(([k, v]) => (
                  <React.Fragment key={k}>
                    <span className="text-gray-500">custom.{k}</span>
                    <span>{String(v)}</span>
                  </React.Fragment>
                ))}
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        open={showReset}
        title="Reset to default mapping?"
        message="This deletes your custom mapping. New leads on this endpoint will go back to the built-in default (sourceNumber → phone, leadName → name)."
        confirmLabel="Reset"
        loading={resetting}
        onCancel={() => setShowReset(false)}
        onConfirm={handleReset}
      />
    </div>
  );
};

export default LeadFieldMappingEditor;